/**
 * Bandeira de uma embarcação a partir do MMSI — funções puras (testadas em
 * `mmsi.test.ts`).
 *
 * Os três primeiros dígitos do MMSI são o MID (Maritime Identification
 * Digits), que a ITU atribui por país. Um país pode ter vários MIDs
 * (o Panamá tem sete), e o MID diz a bandeira do REGISTRO, não onde a
 * embarcação está nem de onde é a tripulação — por isso a interface diz
 * "bandeira", e nunca "país".
 *
 * A tabela cobre a América do Sul inteira, as bandeiras de conveniência que
 * de fato aparecem no corredor amazônico (medido: Camboja, Panamá, Libéria,
 * Ilhas Marshall, Antígua) e as grandes marinhas mercantes. MID desconhecido
 * não é erro: devolve `null` e a interface mostra só o MMSI.
 *
 * Fonte da tabela: ITU, "Table of Maritime Identification Digits"
 * (Lista V / MARS), https://www.itu.int/en/ITU-R/terrestrial/fmd/Pages/mid.aspx
 */

export interface Bandeira {
  /** Nome do país em português. */
  pais: string;
  /** Código ISO 3166-1 alfa-2, usado para desenhar a bandeira. */
  iso: string;
}

/**
 * MID → país. Escrito como uma lista de pares para caber no olho; vira Map na
 * carga do módulo.
 */
const TABELA: readonly (readonly [number, string, string])[] = [
  // ---- América do Sul ----
  [710, 'Brasil', 'BR'],
  [701, 'Argentina', 'AR'],
  [720, 'Bolívia', 'BO'],
  [725, 'Chile', 'CL'],
  [730, 'Colômbia', 'CO'],
  [735, 'Equador', 'EC'],
  [740, 'Ilhas Malvinas', 'FK'],
  [745, 'Guiana Francesa', 'GF'],
  [750, 'Guiana', 'GY'],
  [755, 'Paraguai', 'PY'],
  [760, 'Peru', 'PE'],
  [765, 'Suriname', 'SR'],
  [770, 'Uruguai', 'UY'],
  [775, 'Venezuela', 'VE'],

  // ---- Bandeiras de conveniência e registros abertos ----
  [351, 'Panamá', 'PA'],
  [352, 'Panamá', 'PA'],
  [353, 'Panamá', 'PA'],
  [354, 'Panamá', 'PA'],
  [355, 'Panamá', 'PA'],
  [356, 'Panamá', 'PA'],
  [357, 'Panamá', 'PA'],
  [370, 'Panamá', 'PA'],
  [371, 'Panamá', 'PA'],
  [372, 'Panamá', 'PA'],
  [373, 'Panamá', 'PA'],
  [374, 'Panamá', 'PA'],
  [636, 'Libéria', 'LR'],
  [637, 'Libéria', 'LR'],
  [538, 'Ilhas Marshall', 'MH'],
  [345, 'México', 'MX'],
  [341, 'São Cristóvão e Névis', 'KN'],
  [304, 'Antígua e Barbuda', 'AG'],
  [305, 'Antígua e Barbuda', 'AG'],
  [311, 'Bahamas', 'BS'],
  [312, 'Belize', 'BZ'],
  [314, 'Barbados', 'BB'],
  [319, 'Ilhas Cayman', 'KY'],
  [310, 'Bermudas', 'BM'],
  [306, 'Curaçao', 'CW'],
  [339, 'Jamaica', 'JM'],
  [376, 'São Vicente e Granadinas', 'VC'],
  [377, 'São Vicente e Granadinas', 'VC'],
  [378, 'Ilhas Virgens Britânicas', 'VG'],
  [515, 'Camboja', 'KH'],
  [514, 'Camboja', 'KH'],
  [511, 'Palau', 'PW'],
  [518, 'Ilhas Cook', 'CK'],
  [576, 'Vanuatu', 'VU'],
  [577, 'Vanuatu', 'VU'],
  [616, 'Comores', 'KM'],
  [667, 'Serra Leoa', 'SL'],
  [671, 'Togo', 'TG'],
  [572, 'Tanzânia', 'TZ'],
  [613, 'Camarões', 'CM'],
  [214, 'Moldávia', 'MD'],

  // ---- Europa ----
  [209, 'Chipre', 'CY'],
  [210, 'Chipre', 'CY'],
  [212, 'Chipre', 'CY'],
  [211, 'Alemanha', 'DE'],
  [218, 'Alemanha', 'DE'],
  [215, 'Malta', 'MT'],
  [229, 'Malta', 'MT'],
  [248, 'Malta', 'MT'],
  [249, 'Malta', 'MT'],
  [256, 'Malta', 'MT'],
  [219, 'Dinamarca', 'DK'],
  [220, 'Dinamarca', 'DK'],
  [224, 'Espanha', 'ES'],
  [225, 'Espanha', 'ES'],
  [226, 'França', 'FR'],
  [227, 'França', 'FR'],
  [228, 'França', 'FR'],
  [232, 'Reino Unido', 'GB'],
  [233, 'Reino Unido', 'GB'],
  [234, 'Reino Unido', 'GB'],
  [235, 'Reino Unido', 'GB'],
  [236, 'Gibraltar', 'GI'],
  [237, 'Grécia', 'GR'],
  [239, 'Grécia', 'GR'],
  [240, 'Grécia', 'GR'],
  [241, 'Grécia', 'GR'],
  [244, 'Países Baixos', 'NL'],
  [245, 'Países Baixos', 'NL'],
  [246, 'Países Baixos', 'NL'],
  [247, 'Itália', 'IT'],
  [253, 'Luxemburgo', 'LU'],
  [255, 'Portugal', 'PT'],
  [263, 'Portugal', 'PT'],
  [257, 'Noruega', 'NO'],
  [258, 'Noruega', 'NO'],
  [259, 'Noruega', 'NO'],
  [265, 'Suécia', 'SE'],
  [266, 'Suécia', 'SE'],
  [230, 'Finlândia', 'FI'],
  [205, 'Bélgica', 'BE'],
  [203, 'Áustria', 'AT'],
  [273, 'Rússia', 'RU'],
  [272, 'Ucrânia', 'UA'],
  [271, 'Turquia', 'TR'],
  [207, 'Bulgária', 'BG'],
  [264, 'Romênia', 'RO'],
  [238, 'Croácia', 'HR'],
  [261, 'Polônia', 'PL'],
  [276, 'Estônia', 'EE'],
  [275, 'Letônia', 'LV'],
  [277, 'Lituânia', 'LT'],
  [231, 'Ilhas Faroé', 'FO'],
  [251, 'Islândia', 'IS'],
  [250, 'Irlanda', 'IE'],
  [269, 'Suíça', 'CH'],

  // ---- América do Norte ----
  [338, 'Estados Unidos', 'US'],
  [366, 'Estados Unidos', 'US'],
  [367, 'Estados Unidos', 'US'],
  [368, 'Estados Unidos', 'US'],
  [369, 'Estados Unidos', 'US'],
  [303, 'Estados Unidos (Alasca)', 'US'],
  [316, 'Canadá', 'CA'],

  // ---- Ásia e Oceania ----
  [412, 'China', 'CN'],
  [413, 'China', 'CN'],
  [414, 'China', 'CN'],
  [477, 'Hong Kong', 'HK'],
  [431, 'Japão', 'JP'],
  [432, 'Japão', 'JP'],
  [440, 'Coreia do Sul', 'KR'],
  [441, 'Coreia do Sul', 'KR'],
  [563, 'Singapura', 'SG'],
  [564, 'Singapura', 'SG'],
  [565, 'Singapura', 'SG'],
  [566, 'Singapura', 'SG'],
  [525, 'Indonésia', 'ID'],
  [533, 'Malásia', 'MY'],
  [567, 'Tailândia', 'TH'],
  [574, 'Vietnã', 'VN'],
  [419, 'Índia', 'IN'],
  [405, 'Bangladesh', 'BD'],
  [422, 'Irã', 'IR'],
  [470, 'Emirados Árabes Unidos', 'AE'],
  [471, 'Emirados Árabes Unidos', 'AE'],
  [403, 'Arábia Saudita', 'SA'],
  [503, 'Austrália', 'AU'],
  [512, 'Nova Zelândia', 'NZ'],
  [548, 'Filipinas', 'PH'],

  // ---- África ----
  [601, 'África do Sul', 'ZA'],
  [622, 'Egito', 'EG'],
  [605, 'Argélia', 'DZ'],
  [642, 'Líbia', 'LY'],
  [649, 'Marrocos', 'MA'],
  [657, 'Nigéria', 'NG'],
  [627, 'Gana', 'GH'],
  [645, 'Moçambique', 'MZ'],
  [663, 'Senegal', 'SN'],
  [620, 'Comores (registro)', 'KM'],
];

const POR_MID = new Map<number, Bandeira>(TABELA.map(([mid, pais, iso]) => [mid, { pais, iso }]));

/** O MID do Brasil — a frota regional amazônica. */
export const MID_BRASIL = 710;

/**
 * MID de um MMSI de embarcação (9 dígitos). Devolve `null` para os MMSI que
 * não são de navio: estação costeira (0…), grupo (01…), SAR aéreo (111…),
 * auxiliar de bordo (98…), AtoN/baliza virtual (99…) e MOB/EPIRB (97…).
 * Esses aparecem no AIS e não têm bandeira no sentido usual.
 */
export function midDe(mmsi: number): number | null {
  if (!Number.isFinite(mmsi)) return null;
  const n = Math.trunc(mmsi);
  if (n < 100_000_000 || n > 999_999_999) return null;
  const mid = Math.trunc(n / 1_000_000);
  // 970/972/974 = MOB e EPIRB; 98x = auxiliar; 99x = AtoN.
  if (mid >= 970) return null;
  return mid;
}

/** Bandeira do registro, ou `null` quando o MID é desconhecido ou não se aplica. */
export function bandeiraDe(mmsi: number): Bandeira | null {
  const mid = midDe(mmsi);
  if (mid === null) return null;
  return POR_MID.get(mid) ?? null;
}

export function ehBrasileira(mmsi: number): boolean {
  return midDe(mmsi) === MID_BRASIL;
}

/**
 * O que o MMSI é, quando não é uma embarcação comum. Serve para o popup
 * explicar um ponto que não é navio em vez de mostrar campos vazios.
 */
export type Especie = 'embarcacao' | 'estacaoCosteira' | 'auxiliar' | 'ajudaNavegacao' | 'socorro' | 'aeronave' | 'grupo';

export function especieDe(mmsi: number): Especie {
  if (!Number.isFinite(mmsi)) return 'embarcacao';
  const n = Math.trunc(mmsi);
  const s = String(n).padStart(9, '0');
  if (s.startsWith('111')) return 'aeronave';
  if (s.startsWith('00')) return 'estacaoCosteira';
  if (s.startsWith('0')) return 'grupo';
  if (s.startsWith('98')) return 'auxiliar';
  if (s.startsWith('99')) return 'ajudaNavegacao';
  if (s.startsWith('97')) return 'socorro';
  return 'embarcacao';
}

export const NOME_ESPECIE: Readonly<Record<Especie, string>> = {
  embarcacao: 'Embarcação',
  estacaoCosteira: 'Estação costeira',
  auxiliar: 'Embarcação auxiliar',
  ajudaNavegacao: 'Ajuda à navegação (boia ou baliza)',
  socorro: 'Baliza de socorro',
  aeronave: 'Aeronave de busca e salvamento',
  grupo: 'Chamada de grupo',
};

/** Bandeira em emoji, a partir do ISO alfa-2 (indicadores regionais Unicode). */
export function emojiBandeira(iso: string): string {
  if (!/^[A-Za-z]{2}$/.test(iso)) return '';
  const base = 0x1f1e6 - 'A'.charCodeAt(0);
  return String.fromCodePoint(...[...iso.toUpperCase()].map((c) => base + c.charCodeAt(0)));
}
